import { useCallback, useMemo, useState } from 'react'
import {
  setupEnrichment,
  disableEnrichment as disableEnrichmentCommand,
  verifyEnrichment,
  isEnrichmentComplete,
  missingEnrichmentKeys,
  ALL_ENRICHMENT_FIELD_KEYS,
  type CrmName,
  type EnrichmentConfig,
  type EnrichmentFieldKey,
} from '../lib/crm'
import { useCrmConnections } from './useCrmConnections'

export function useEnrichment(crmName: CrmName | null) {
  const { connections } = useCrmConnections()
  const [settingUp, setSettingUp] = useState(false)
  const [verifying, setVerifying] = useState(false)

  // Derive config from the connection — no extra round-trip, always fresh
  const config: EnrichmentConfig | null = useMemo(() => {
    if (crmName !== 'affinity') return null
    const conn = connections.affinity
    if (!conn) return null
    return {
      enabled: Boolean(conn.enrichmentEnabled),
      fieldIds: conn.enrichmentFieldIds ?? null,
      fieldsSchemaVersion: conn.enrichmentFieldsSchemaVersion ?? null,
      setupAt: conn.enrichmentSetupAt ?? null,
    }
  }, [crmName, connections.affinity])

  // Stabilize fieldIds dependency: cross-tab broadcasts produce new object refs
  // with identical contents. JSON-serializing 14 keys is cheap and produces a
  // stable string so downstream memos don't re-derive on referentially-different-
  // but-identical updates.
  const fieldIdsKey = useMemo(
    () => (config?.fieldIds ? JSON.stringify(config.fieldIds) : 'null'),
    [config?.fieldIds],
  )

  // Derived completeness — precomputed so components don't re-derive
  const complete = useMemo(
    () => isEnrichmentComplete(config?.fieldIds ?? null),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [fieldIdsKey],
  )
  const missingKeys: EnrichmentFieldKey[] = useMemo(
    () => (config?.enabled ? missingEnrichmentKeys(config.fieldIds ?? null) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [config?.enabled, fieldIdsKey],
  )
  const totalFieldCount = ALL_ENRICHMENT_FIELD_KEYS.length

  const enable = useCallback(async () => {
    if (!crmName) return { ok: false as const, error: 'No CRM connected' }
    setSettingUp(true)
    try {
      const result = await setupEnrichment(crmName)
      // Connection state will update via broadcastCrmChange → useCrmConnections refresh
      return { ok: true as const, fieldCount: result.fieldCount }
    } catch (e) {
      const err = e as Error & { partial?: boolean; failures?: unknown }
      return {
        ok: false as const,
        error: err.message,
        partial: err.partial === true,
      }
    } finally {
      setSettingUp(false)
    }
  }, [crmName])

  const disable = useCallback(async () => {
    if (!crmName) return { ok: false as const }
    try {
      await disableEnrichmentCommand(crmName)
      return { ok: true as const }
    } catch (e) {
      return { ok: false as const, error: e instanceof Error ? e.message : 'Disable failed' }
    }
  }, [crmName])

  const verify = useCallback(async () => {
    if (!crmName) return null
    setVerifying(true)
    try {
      return await verifyEnrichment(crmName)
    } finally {
      setVerifying(false)
    }
  }, [crmName])

  return {
    config,
    complete,
    missingKeys,
    totalFieldCount,
    settingUp,
    verifying,
    enable,
    disable,
    verify,
  }
}
